import React, { forwardRef, useState } from "react";
import { Search, Eye, EyeOff, Copy, ChevronDown } from "lucide-react";

/* =======================
   Styles
======================= */
import styles from "./Input.module.scss";

const Input = forwardRef(
  (
    {
      label,
      type = "text",
      placeholder,
      options = [],
      showPasswordToggle = false,
      error = "",
      required = false,
      className = "",
      onlyLabel = false,
      showCopy = false,
      copyText = "",
      onCopySuccess,
      showSelectIcon = true,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const isPassword = type === "password";
    const isSearch = type === "search";
    const isSelect = type === "select";
    const isTextarea = type === "textarea";

    // Determine if we need right padding for an icon
    const hasRightIcon = (isPassword && showPasswordToggle) || showCopy;

    const copyToClipboard = async (text) => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        }

        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const success = document.execCommand("copy");
        document.body.removeChild(textArea);

        return success;
      } catch (err) {
        console.error("Copy failed", err);
        return false;
      }
    };

    const finalType =
      isPassword && showPasswordToggle
        ? showPassword
          ? "text"
          : "password"
        : type;

    // Base Control Classes
    const controlClasses = [
      styles["admin-input__control"],
      isSearch ? styles["admin-input__control--search"] : "",
      hasRightIcon ? styles["admin-input__control--has-right-icon"] : "",
      isTextarea ? styles["admin-input__control--textarea"] : "",
      isSelect ? styles["admin-input__control--select"] : "",
      error ? styles["admin-input__control--error"] : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={`${styles["admin-input__group"]} ${className}`}>
        {/* LABEL */}
        {label && (
          <label className={styles["admin-input__label"]}>
            {label}{" "}
            {required && (
              <span className={styles["admin-input__required"]}>*</span>
            )}
          </label>
        )}

        {/* ONLY LABEL MODE */}
        {!onlyLabel && (
          <div className={styles["admin-input__wrapper"]}>
            {/* Search Icon (Left) */}
            {isSearch && (
              <Search size={18} className={styles["admin-input__icon-left"]} />
            )}

            {/* INPUT (Standard & Search) */}
            {!isSelect && !isTextarea && (
              <input
                ref={ref}
                type={finalType}
                placeholder={placeholder}
                className={controlClasses}
                {...props}
              />
            )}

            {/* SELECT */}
            {isSelect && (
              <>
                <select ref={ref} className={controlClasses} {...props}>
                  <option value="" disabled hidden>
                    {placeholder || "Select an option"}
                  </option>
                  {options.map((opt, index) => (
                    <option key={index} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {showSelectIcon && (
                  <ChevronDown
                    size={18}
                    className={styles["admin-input__icon-select"]}
                  />
                )}
              </>
            )}

            {/* TEXTAREA */}
            {isTextarea && (
              <textarea
                ref={ref}
                className={controlClasses}
                placeholder={placeholder}
                rows="4"
                {...props}
              />
            )}

            {/* Password Toggle Icon (Right) */}
            {isPassword && showPasswordToggle && (
              <button
                type="button"
                className={styles["admin-input__icon-right"]}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            )}

            {/* Copy Icon (Right) */}
            {showCopy && (
              <button
                type="button"
                className={styles["admin-input__icon-right"]}
                onClick={async () => {
                  const success = await copyToClipboard(copyText || "");
                  if (success && onCopySuccess) {
                    onCopySuccess();
                  }
                }}
                aria-label="Copy to clipboard"
              >
                <Copy size={16} />
              </button>
            )}
          </div>
        )}

        {/* ERROR MESSAGE */}
        {error && <p className={styles["admin-input__error-text"]}>{error}</p>}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
